import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  IconButton,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  InputAdornment,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Restaurant as RestaurantIcon,
  Favorite as FavoriteIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { calorieAIService } from '../../services/calorieAI/calorieAIService';

const FoodDatabase = ({ onBack, onFoodAdded }) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [customFoods, setCustomFoods] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedFood, setSelectedFood] = useState(null);
  const [foodQuantity, setFoodQuantity] = useState(1);
  const [mealType, setMealType] = useState('');
  const [loading, setLoading] = useState(false);

  const mealTypes = [
    { value: 'breakfast', label: '早餐 🌅' },
    { value: 'lunch', label: '午餐 🌞' },
    { value: 'snack', label: '零食 🍪' },
    { value: 'dinner', label: '晚餐 🌙' },
    { value: 'latenight', label: '宵夜 🌃' },
    { value: 'other', label: '其他 🍽️' }
  ];

  const [newCustomFood, setNewCustomFood] = useState({
    name: '',
    calories_per_unit: '',
    unit: '份',
    protein: '',
    carbs: '',
    fat: '',
    category: '自定義'
  });

  useEffect(() => {
    loadInitialData();
    setMealType(getCurrentMealType());
  }, []);

  useEffect(() => {
    if (searchQuery) {
      performSearch();
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const getCurrentMealType = () => {
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 11) return 'breakfast';
    if (hour >= 11 && hour < 15) return 'lunch';
    if (hour >= 18 && hour < 22) return 'dinner';
    if (hour >= 22 || hour < 3) return 'latenight';
    return 'snack';
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [categoriesData, customFoodsData] = await Promise.all([
        calorieAIService.getFoodCategories(),
        calorieAIService.getCustomFoods()
      ]);
      
      setCategories(categoriesData);
      setCustomFoods(customFoodsData);
    } catch (error) {
      console.error('載入初始數據失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const performSearch = async () => {
    try {
      const results = await calorieAIService.searchFoodDatabase(searchQuery);
      setSearchResults(results);
    } catch (error) {
      console.error('搜尋失敗:', error);
    }
  };

  const handleFoodSelect = (food) => {
    setSelectedFood(food);
    setFoodQuantity(1);
    setShowConfirmDialog(true);
  };

  const handleAddFood = async () => {
    try {
      setLoading(true);
      
      const foodRecord = {
        food_name: selectedFood.name,
        quantity: foodQuantity,
        unit: selectedFood.unit || '份',
        calories: (selectedFood.calories_per_100g || selectedFood.calories_per_unit || 0) * foodQuantity,
        protein: (selectedFood.protein_per_100g || selectedFood.protein || 0) * foodQuantity,
        carbs: (selectedFood.carbs_per_100g || selectedFood.carbs || 0) * foodQuantity,
        fat: (selectedFood.fat_per_100g || selectedFood.fat || 0) * foodQuantity,
        meal_type: mealType,
        recorded_at: new Date().toISOString()
      };

      await calorieAIService.addFoodRecord(foodRecord);
      setShowConfirmDialog(false);
      onFoodAdded();
    } catch (error) {
      console.error('添加食物記錄失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomFood = async () => {
    try {
      setLoading(true);
      await calorieAIService.addCustomFood(newCustomFood);
      
      // 重新載入自定義食物列表
      const updatedCustomFoods = await calorieAIService.getCustomFoods();
      setCustomFoods(updatedCustomFoods);
      
      // 重置表單
      setNewCustomFood({
        name: '',
        calories_per_unit: '',
        unit: '份',
        protein: '',
        carbs: '',
        fat: '',
        category: '自定義'
      });
      
      setShowAddDialog(false);
    } catch (error) {
      console.error('添加自定義食物失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderSearchTab = () => (
    <Box sx={{ p: 2 }}>
      <TextField
        fullWidth
        placeholder="搜尋食物名稱..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      {searchResults.length > 0 && (
        <Typography variant="h6" sx={{ mb: 2, color: '#333' }}>
          搜尋結果 ({searchResults.length})
        </Typography>
      )}

      <Grid container spacing={2}>
        {searchResults.map((food) => (
          <Grid item xs={12} key={food.id}>
            <Card 
              sx={{ 
                borderRadius: '12px',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f5f5f5' }
              }}
              onClick={() => handleFoodSelect(food)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {food.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      {food.category}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip 
                        label={`${food.calories_per_100g || food.calories_per_unit || 0} 卡`}
                        size="small"
                        sx={{ backgroundColor: '#FFE0E0', color: '#FF6B6B' }}
                      />
                      <Chip 
                        label={`蛋白質 ${food.protein_per_100g || food.protein || 0}g`}
                        size="small"
                        sx={{ backgroundColor: '#E8F5E8', color: '#4CAF50' }}
                      />
                    </Box>
                  </Box>
                  <RestaurantIcon sx={{ color: '#FF6B6B', fontSize: 32 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {searchQuery && searchResults.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="textSecondary">
            找不到相關食物，試試其他關鍵字或添加自定義食物
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderCategoryTab = () => (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, color: '#333' }}>
        食物分類 📂
      </Typography>

      <Grid container spacing={2}>
        {categories.map((category) => (
          <Grid item xs={6} sm={4} key={category}>
            <Paper
              sx={{
                p: 2,
                textAlign: 'center',
                borderRadius: '12px',
                cursor: 'pointer',
                backgroundColor: selectedCategory === category ? '#FFE0E0' : 'white',
                '&:hover': { backgroundColor: '#f5f5f5' }
              }}
              onClick={() => setSelectedCategory(category)}
            >
              <CategoryIcon sx={{ fontSize: 32, color: '#FF6B6B', mb: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {category}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {selectedCategory && (
        <CategoryFoodList 
          category={selectedCategory} 
          onFoodSelect={handleFoodSelect}
        />
      )}
    </Box>
  );

  const renderCustomTab = () => (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ color: '#333' }}>
          自定義食物 ⭐
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowAddDialog(true)}
          sx={{
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #FF6B6B, #FF8E8E)',
          }}
        >
          添加
        </Button>
      </Box>

      <Grid container spacing={2}>
        {customFoods.map((food) => (
          <Grid item xs={12} key={food.id}>
            <Card 
              sx={{ 
                borderRadius: '12px',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f5f5f5' }
              }}
              onClick={() => handleFoodSelect(food)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {food.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      每{food.unit} • {food.category}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip 
                        label={`${food.calories_per_unit} 卡`}
                        size="small"
                        sx={{ backgroundColor: '#FFE0E0', color: '#FF6B6B' }}
                      />
                      <Chip 
                        label={`蛋白質 ${food.protein}g`}
                        size="small"
                        sx={{ backgroundColor: '#E8F5E8', color: '#4CAF50' }}
                      />
                    </Box>
                  </Box>
                  <FavoriteIcon sx={{ color: '#FF6B6B', fontSize: 32 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {customFoods.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="textSecondary">
            還沒有自定義食物，點擊上方按鈕添加
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh'}}>
      {/* 頂部導航 */}
      <Paper sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center',
        backgroundColor: 'white',
        borderRadius: 0,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          食物資料庫
        </Typography>
      </Paper>

      {/* 標籤頁 */}
      <Paper sx={{ backgroundColor: 'white' }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              color: '#666',
              '&.Mui-selected': {
                color: '#FF6B6B',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#FF6B6B',
            },
          }}
        >
          <Tab label="搜尋" />
          <Tab label="分類" />
          <Tab label="自定義" />
        </Tabs>
      </Paper>

      {/* 標籤頁內容 */}
      <Box sx={{ pb: 10 }}>
        {currentTab === 0 && renderSearchTab()}
        {currentTab === 1 && renderCategoryTab()}
        {currentTab === 2 && renderCustomTab()}
      </Box>

      {/* 添加自定義食物對話框 */}
      <Dialog 
        open={showAddDialog} 
        onClose={() => setShowAddDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>添加自定義食物</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="食物名稱"
                value={newCustomFood.name}
                onChange={(e) => setNewCustomFood({ ...newCustomFood, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="卡路里"
                type="number"
                value={newCustomFood.calories_per_unit}
                onChange={(e) => setNewCustomFood({ ...newCustomFood, calories_per_unit: e.target.value === '' ? '' : e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="單位"
                value={newCustomFood.unit}
                onChange={(e) => setNewCustomFood({ ...newCustomFood, unit: e.target.value })}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="蛋白質 (g)"
                type="number"
                value={newCustomFood.protein}
                onChange={(e) => setNewCustomFood({ ...newCustomFood, protein: e.target.value === '' ? '' : e.target.value })}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="碳水 (g)"
                type="number"
                value={newCustomFood.carbs}
                onChange={(e) => setNewCustomFood({ ...newCustomFood, carbs: e.target.value === '' ? '' : e.target.value })}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="脂肪 (g)"
                type="number"
                value={newCustomFood.fat}
                onChange={(e) => setNewCustomFood({ ...newCustomFood, fat: e.target.value === '' ? '' : e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>取消</Button>
          <Button 
            onClick={handleAddCustomFood}
            variant="contained"
            disabled={!newCustomFood.name || !newCustomFood.calories_per_unit}
            sx={{ background: 'linear-gradient(135deg, #FF6B6B, #FF8E8E)' }}
          >
            添加
          </Button>
        </DialogActions>
      </Dialog>

      {/* 確認添加食物對話框 */}
      <Dialog 
        open={showConfirmDialog} 
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>添加到飲食記錄</DialogTitle>
        <DialogContent>
          {selectedFood && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {selectedFood.name}
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="數量"
                    type="number"
                    value={foodQuantity}
                    onChange={(e) => setFoodQuantity(e.target.value === '' ? '' : parseFloat(e.target.value))}
                    inputProps={{ min: 0.1, step: 0.1 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    select
                    label="餐型"
                    value={mealType}
                    onChange={(e) => setMealType(e.target.value)}
                  >
                    {mealTypes.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  營養成分預覽:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Typography variant="body2" sx={{ textAlign: 'center' }}>
                      <strong>{Math.round((selectedFood.calories_per_100g || selectedFood.calories_per_unit || 0) * foodQuantity)}</strong><br/>
                      卡路里
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" sx={{ textAlign: 'center' }}>
                      <strong>{((selectedFood.protein_per_100g || selectedFood.protein || 0) * foodQuantity).toFixed(1)}g</strong><br/>
                      蛋白質
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" sx={{ textAlign: 'center' }}>
                      <strong>{((selectedFood.carbs_per_100g || selectedFood.carbs || 0) * foodQuantity).toFixed(1)}g</strong><br/>
                      碳水
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" sx={{ textAlign: 'center' }}>
                      <strong>{((selectedFood.fat_per_100g || selectedFood.fat || 0) * foodQuantity).toFixed(1)}g</strong><br/>
                      脂肪
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>取消</Button>
          <Button 
            onClick={handleAddFood}
            variant="contained"
            disabled={loading}
            sx={{ background: 'linear-gradient(135deg, #4CAF50, #66BB6A)' }}
          >
            確認添加
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// 分類食物列表組件
const CategoryFoodList = ({ category, onFoodSelect }) => {
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCategoryFoods();
  }, [category]);

  const loadCategoryFoods = async () => {
    try {
      setLoading(true);
      const categoryFoods = await calorieAIService.getFoodsByCategory(category);
      setFoods(categoryFoods);
    } catch (error) {
      console.error('載入分類食物失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
        {category} 類食物
      </Typography>
      
      <Grid container spacing={2}>
        {foods.map((food) => (
          <Grid item xs={12} key={food.id}>
            <Card 
              sx={{ 
                borderRadius: '12px',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f5f5f5' }
              }}
              onClick={() => onFoodSelect(food)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {food.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip 
                        label={`${food.calories_per_100g} 卡/100g`}
                        size="small"
                        sx={{ backgroundColor: '#FFE0E0', color: '#FF6B6B' }}
                      />
                      <Chip 
                        label={`蛋白質 ${food.protein_per_100g}g`}
                        size="small"
                        sx={{ backgroundColor: '#E8F5E8', color: '#4CAF50' }}
                      />
                    </Box>
                  </Box>
                  <RestaurantIcon sx={{ color: '#FF6B6B', fontSize: 32 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {foods.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography color="textSecondary">
            此分類暫無食物
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default FoodDatabase;